/**
 * Internationalization (i18n) System
 *
 * This module provides translation functions for multi-language support.
 * Translations are loaded from JSON files and cached for performance.
 */

export type SupportedLanguage = "ar" | "en";

type TranslationValue = string | Record<string, unknown>;
type TranslationObject = Record<string, TranslationValue>;

const translations: Partial<Record<SupportedLanguage, TranslationObject>> = {};

const translationLoaders: Record<SupportedLanguage, () => Promise<{ default: unknown }>> = {
  ar: () => import("./locales/ar.json"),
  en: () => import("./locales/en.json"),
};

// Default language - can be overridden by TenantProvider
let currentLanguage: SupportedLanguage = "ar";

/** Register a locale dictionary without coupling the synchronous translator to both bundles. */
export function registerTranslations(language: SupportedLanguage, values: unknown): void {
  translations[language] = values as TranslationObject;
}

/** Load only the active dashboard locale before React renders. */
export async function loadTranslations(language: SupportedLanguage): Promise<void> {
  if (!translations[language]) {
    const module = await translationLoaders[language]();
    registerTranslations(language, module.default);
  }
  currentLanguage = language;
}

/**
 * Get the current language
 */
export function getLanguage(): SupportedLanguage {
  return currentLanguage;
}

/**
 * Get a nested value from an object using dot notation
 */
function getNestedEntry(obj: TranslationObject, path: string): unknown {
  if (!path) {
    return undefined;
  }
  const keys = path.split(".");
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current === "object" && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }

  return current;
}

function getNestedValue(obj: TranslationObject, path: string): string | undefined {
  const value = getNestedEntry(obj, path);
  return typeof value === "string" ? value : undefined;
}

/**
 * Interpolate variables in a translation string
 * Supports both {variable} and {{variable}} syntax
 */
function interpolate(str: string, params?: Record<string, string | number>): string {
  if (!params) return str;

  // First replace double-brace {{var}}, then single-brace {var}
  let result = str.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return params[key]?.toString() ?? `{{${key}}}`;
  });
  result = result.replace(/\{(\w+)\}/g, (_, key) => {
    return params[key]?.toString() ?? `{${key}}`;
  });
  return result;
}

/**
 * Translate a key to the current language
 *
 * @param key - Dot-notation key (e.g., "orders.states.Draft")
 * @param params - Optional interpolation parameters
 * @param lang - Optional language override
 * @returns Translated string or the key if not found
 *
 * @example
 * t("orders.title") // "Orders"
 * t("refunds.transitions.settled", { amount: "$100" }) // "Refund settled for $100"
*/
export function t(
  key: string,
  params?: Record<string, string | number>,
  lang?: SupportedLanguage
): string {
  if (!key) {
    console.warn("[i18n] Missing translation key");
    return "";
  }
  const language = lang || currentLanguage;
  const translationObj = translations[language];

  if (!translationObj) {
    console.warn(`[i18n] Language "${language}" not found, falling back to key`);
    return key;
  }

  const value = getNestedValue(translationObj, key);

  if (value === undefined) {
    // Try fallback to English if not found in current language
    if (language !== "en" && translations.en) {
      const fallbackValue = getNestedValue(translations.en, key);
      if (fallbackValue) {
        return interpolate(fallbackValue, params);
      }
    }
    console.warn(`[i18n] Translation key "${key}" not found`);
    return key;
  }

  return interpolate(value, params);
}

/**
 * Create a scoped translator for a specific namespace
 *
 * @param namespace - The namespace prefix (e.g., "orders")
 * @returns A function that translates keys within that namespace
 *
 * @example
 * const tOrders = createScopedT("orders");
 * tOrders("title") // equivalent to t("orders.title")
 * tOrders("states.Draft") // equivalent to t("orders.states.Draft")
 */
export function createScopedT(namespace: string) {
  return (key: string, params?: Record<string, string | number>, lang?: SupportedLanguage): string => {
    return t(`${namespace}.${key}`, params, lang);
  };
}

/**
 * Get all translations for a namespace
 * Useful for mapping over states, types, etc.
 */
export function getTranslations(
  namespace: string,
  lang?: SupportedLanguage
): Record<string, string> {
  const language = lang || currentLanguage;
  const translationObj = translations[language];
  if (!translationObj) {
    return {};
  }
  const value = getNestedEntry(translationObj, namespace);

  if (typeof value === "object" && value !== null) {
    // Filter to only string values
    const result: Record<string, string> = {};
    for (const [key, val] of Object.entries(value)) {
      if (typeof val === "string") {
        result[key] = val;
      }
    }
    return result;
  }

  return {};
}
